import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Grid,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  Alert,
} from '@mui/material';
import {
  Tag as TagIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  Home as HomeIcon,
  NavigateNext,
  Article as ArticleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useParams, Navigate } from 'react-router-dom';
import { getPostsByTag, getAllTagsWithCounts, getTrendingPosts } from '../utils/searchUtils';
import { authors } from '../data/blogPosts';

const TagPage = () => {
  const { tag } = useParams();
  const [tagPosts, setTagPosts] = useState([]);
  const [relatedTags, setRelatedTags] = useState([]);
  
  const allTags = getAllTagsWithCounts();
  const trendingPosts = getTrendingPosts(4);
  const currentTagInfo = allTags.find(t => t.tag === tag);

  useEffect(() => {
    if (tag) {
      const posts = getPostsByTag(tag);
      setTagPosts(posts);
      
      // Find related tags (tags that appear in the same posts)
      const tagCounts = {};
      posts.forEach(post => {
        post.tags.forEach(postTag => {
          if (postTag !== tag) {
            tagCounts[postTag] = (tagCounts[postTag] || 0) + 1;
          }
        });
      });
      
      const sortedRelatedTags = Object.entries(tagCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 8)
        .map(([tagName, count]) => ({ tag: tagName, count }));
      
      setRelatedTags(sortedRelatedTags);
    }
  }, [tag]);

  if (!currentTagInfo) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReadingTime = (post) => {
    if (post.readingTime) return post.readingTime;
    const wordsPerMinute = 200;
    const words = post.content.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  const TagPostCard = ({ post }) => {
    const author = authors[post.authors[0]];
    
    return (
      <Card 
        elevation={0}
        sx={{ 
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 2,
          },
          transition: 'all 0.2s ease',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <Box>
                  <Typography 
                    variant="h5" 
                    component={RouterLink}
                    to={`/blog/${post.slug}`}
                    sx={{ 
                      textDecoration: 'none',
                      color: 'text.primary',
                      '&:hover': { color: 'primary.main' },
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {post.title}
                  </Typography>
                </Box>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ 
                    lineHeight: 1.6,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {post.excerpt}
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={author.image}
                      alt={author.name}
                      sx={{ width: 24, height: 24 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {author.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(post.date)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimeIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                      {getReadingTime(post)} min read
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Stack spacing={2} sx={{ height: '100%', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                  {post.tags.map((postTag) => (
                    <Chip
                      key={postTag}
                      label={postTag}
                      component={RouterLink}
                      to={`/blog/tag/${postTag}`}
                      clickable
                      size="small"
                      variant={postTag === tag ? 'filled' : 'outlined'}
                      color={postTag === tag ? 'primary' : 'default'}
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Stack>
                
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <IconButton size="small">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small">
                    <BookmarkIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 4 }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/blog"
          sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
        >
          <ArticleIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Blog
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TagIcon sx={{ fontSize: 18 }} />
          {tag}
        </Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={4}>
            {/* Tag Header */}
            <Paper 
              sx={{ 
                p: 4, 
                background: theme => theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1), rgba(66, 165, 245, 0.05))'
                  : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05), rgba(66, 165, 245, 0.02))',
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 3,
              }}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.contrastText',
                    }}
                  >
                    <TagIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                      {tag}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {tagPosts.length} post{tagPosts.length !== 1 ? 's' : ''} tagged with "{tag}"
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Browse all articles related to {tag}. Find insights, tutorials, and updates on this topic.
                </Typography>

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/blog/search"
                    startIcon={<SearchIcon />}
                  >
                    Advanced Search
                  </Button>
                  <Button
                    variant="text"
                    component={RouterLink}
                    to="/blog"
                    startIcon={<ArticleIcon />}
                  >
                    All Posts
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {/* Posts List */}
            <Box>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Posts Tagged "{tag}"
              </Typography>
              
              {tagPosts.length === 0 ? (
                <Alert severity="info">
                  No posts found with this tag. 
                  <Button component={RouterLink} to="/blog" sx={{ ml: 1 }}>
                    Browse all posts
                  </Button>
                </Alert>
              ) : (
                <Stack spacing={0}>
                  {tagPosts.map((post, index) => (
                    <React.Fragment key={post.slug}>
                      <TagPostCard post={post} />
                      {index < tagPosts.length - 1 && <Divider sx={{ my: 0 }} />}
                    </React.Fragment>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Related Tags */}
            {relatedTags.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Related Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {relatedTags.map(({ tag: relatedTag, count }) => (
                    <Chip
                      key={relatedTag}
                      label={`${relatedTag} (${count})`}
                      component={RouterLink}
                      to={`/blog/tag/${relatedTag}`}
                      clickable
                      variant="outlined"
                      size="small"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Paper>
            )}

            {/* All Tags */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                All Tags
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {allTags.slice(0, 15).map(({ tag: allTag, count }) => (
                  <Chip
                    key={allTag}
                    label={`${allTag} (${count})`}
                    component={RouterLink}
                    to={`/blog/tag/${allTag}`}
                    clickable
                    variant={allTag === tag ? 'filled' : 'outlined'}
                    color={allTag === tag ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
              </Stack>
              <Button 
                component={RouterLink} 
                to="/blog/search" 
                sx={{ mt: 2 }}
                fullWidth
                variant="outlined"
              >
                View All Tags
              </Button>
            </Paper>

            {/* Trending Posts */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon />
                Trending Posts
              </Typography>
              <Stack spacing={2}>
                {trendingPosts.map((post) => (
                  <Box key={post.slug}>
                    <Typography 
                      variant="body2" 
                      component={RouterLink}
                      to={`/blog/${post.slug}`}
                      sx={{ 
                        textDecoration: 'none',
                        color: 'text.primary',
                        '&:hover': { color: 'primary.main' },
                        fontWeight: 500,
                        lineHeight: 1.3,
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {formatDate(post.date)} • {getReadingTime(post)} min read
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TagPage;